import { restrictedCountryBanks } from 'data';

import { CoverStatus } from '../../../../atoms/funding-cover-view';
import { IRestrictedBanks, FamilySponsorType } from '../../../../../types/index.d';

type Fields = {
  sponsorType: string;
  accountDesc: string;
  accountType: string;
  jointType: string;
  accountFirstName: string;
  accountSurname: string;
  otherReason: string;
  country: string;
  bankName: string;
  totalAmount: number;
  sponsorshipAmount: number;
  fundsOverThreshold: boolean;
  isOtherBankName: boolean;
  canWithdrawImmediately: boolean | undefined;
};

type Visible = Record<
  | 'sponsorType'
  | 'accountDesc'
  | 'jointType'
  | 'accountOwner'
  | 'accountFirstName'
  | 'accountSurname'
  | 'otherReason'
  | 'country'
  | 'bankName'
  | 'bankOtherName'
  | 'totalAmount'
  | 'sponsorshipAmount'
  | 'fundingCover'
  | 'canWithdrawImmediately',
  boolean
>;

function isAccountGuardian(accountType: string, jointType: string) {
  if (accountType === 'Joint') {
    return ['Mother', 'Father', 'Guardian', 'Spouse'].includes(jointType);
  } else {
    return ['Mother', 'Father', 'Guardian', 'Spouse'].includes(accountType);
  }
}

function isAccountOther(accountType: string, jointType: string) {
  if (accountType === 'Joint') {
    return jointType === 'Other';
  } else {
    return accountType === 'Other';
  }
}

export function familySponsorTypeLabel(familySponsorType: string) {
  switch (familySponsorType) {
    case FamilySponsorType.STANDARD:
      return 'Standard Bank Account';
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit';
    case FamilySponsorType.SAVINGS:
      return 'Savings Account';
    case FamilySponsorType.INVESTMENT:
      return 'Investment Account';
    case FamilySponsorType.PENSION:
      return 'Pension Account';
    default:
      return '';
  }
}

export function fromFamilySponsorTypeLabel(familySponsorType: string) {
  switch (familySponsorType) {
    case 'Standard Bank Account':
      return FamilySponsorType.STANDARD;
    case 'Term Deposit':
      return FamilySponsorType.TERM_DEPOSIT;
    case 'Savings Account':
      return FamilySponsorType.SAVINGS;
    case 'Investment Account':
      return FamilySponsorType.INVESTMENT;
    case 'Pension Account':
      return FamilySponsorType.PENSION;
    default:
      return '';
  }
}

export function getAccountDesc(accountType: string) {
  if (accountType === 'Mine') {
    return 'My own account';
  } else if (accountType === 'Joint') {
    return 'My joint account with someone else';
  } else if (accountType !== '') {
    return "Someone else's account";
  } else {
    return '';
  }
}

export function getCountryBanks(country: string): string[] {
  const banks = restrictedCountryBanks as IRestrictedBanks;
  const countryBanks = country ? banks[country] || [] : [];
  return countryBanks.map(x => x.bank);
}

export function isRestrictedCountry(country: string) {
  return getCountryBanks(country).length > 0;
}

export function getAccountNameString(accountType: string, jointType: string) {
  if (accountType === 'Joint') {
    switch (jointType) {
      case 'Father':
        return 'Father';
      case 'Mother':
        return 'Mother';
      case 'Guardian':
        return 'Guardian';
      case 'Spouse':
        return 'Partner';
      default:
        return '';
    }
  } else {
    switch (accountType) {
      case 'Father':
        return 'Father';
      case 'Mother':
        return 'Mother';
      case 'Guardian':
        return 'Guardian';
      case 'Spouse':
        return 'Partner';
      default:
        return '';
    }
  }
}

export function accountTypeLabel(accountType: string) {
  switch (accountType) {
    case 'Mine':
      return 'Mine';
    case 'Father':
      return 'Father';
    case 'Mother':
      return 'Mother';
    case 'Guardian':
      return 'Guardian';
    case 'Spouse':
      return 'Partner';
    case 'Joint':
      return 'Joint';
    case 'Other':
      return 'Other';
    default:
      return '';
  }
}

export function getCoverStatus(
  accountType: string,
  jointType: string,
  canWithdrawImmediately: boolean | undefined,
  hasPartner: boolean
) {
  if (canWithdrawImmediately === false) {
    // if cannot withdraw, funds are not accepted
    return {
      courseFees: CoverStatus.INVALID,
      studentLivingCost: CoverStatus.INVALID,
      spouseLivingCost: CoverStatus.INVALID,
      childrenLivingCost: CoverStatus.INVALID
    };
  } else if (accountType === 'Spouse') {
    // Partner funds apply to all cost items, but only if partner
    // is either applying for a UK visa or has a UK visa (hasPartner === true)
    if (hasPartner) {
      return {
        courseFees: CoverStatus.VALID,
        studentLivingCost: CoverStatus.VALID,
        spouseLivingCost: CoverStatus.VALID,
        childrenLivingCost: CoverStatus.VALID
      };
    } else {
      return {
        courseFees: CoverStatus.INVALID,
        studentLivingCost: CoverStatus.INVALID,
        spouseLivingCost: CoverStatus.VALID,
        childrenLivingCost: CoverStatus.VALID
      };
    }
  } else if (accountType === 'Other') {
    return {
      courseFees: CoverStatus.UNKNOWN,
      studentLivingCost: CoverStatus.UNKNOWN,
      spouseLivingCost: CoverStatus.UNKNOWN,
      childrenLivingCost: CoverStatus.UNKNOWN
    };
  } else if (['Mother', 'Father', 'Guardian'].includes(accountType)) {
    return {
      courseFees: CoverStatus.VALID,
      studentLivingCost: CoverStatus.VALID,
      spouseLivingCost: CoverStatus.INVALID,
      childrenLivingCost: CoverStatus.INVALID
    };
  } else {
    return {
      courseFees: CoverStatus.VALID,
      studentLivingCost: CoverStatus.VALID,
      spouseLivingCost: CoverStatus.VALID,
      childrenLivingCost: CoverStatus.VALID
    };
  }
}

export function getPageStatus(fields: Fields): {
  complete: boolean;
  visible: Visible;
} {
  const {
    sponsorType,
    accountDesc,
    accountType,
    jointType,
    accountFirstName,
    accountSurname,
    otherReason,
    country,
    bankName,
    totalAmount,
    sponsorshipAmount,
    fundsOverThreshold,
    isOtherBankName,
    canWithdrawImmediately
  } = fields;

  const complete: boolean = (() => {
    if (sponsorType === '') return false;
    if (accountDesc === '') return false;
    if (accountType === '') return false;
    if (accountType === 'Joint' && jointType === '') return false;
    if (isAccountGuardian(accountType, jointType) && (accountFirstName === '' || accountSurname === '')) return false;
    if (isAccountOther(accountType, jointType) && otherReason === '') return false;
    if (country === '') return false;
    if (isRestrictedCountry(country) && bankName === '') return false;
    if (totalAmount === 0) return false;
    if (totalAmount < sponsorshipAmount) return false;
    if (fundsOverThreshold) return false;
    if (canWithdrawImmediately !== true) return false;

    return true;
  })();

  const visible: Visible = {
    sponsorType: true,
    accountDesc: true,
    jointType: accountType === 'Joint',
    accountOwner: accountDesc === "Someone else's account",
    accountFirstName: isAccountGuardian(accountType, jointType),
    accountSurname: isAccountGuardian(accountType, jointType),
    otherReason: isAccountOther(accountType, jointType),
    country: true,
    bankName: isRestrictedCountry(country) && !isOtherBankName,
    bankOtherName: isRestrictedCountry(country) && isOtherBankName,
    totalAmount: true,
    sponsorshipAmount: true,
    fundingCover: accountType === 'Joint' ? jointType !== '' : accountType !== '',
    canWithdrawImmediately: true
  };

  return {
    complete,
    visible
  };
}
